import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router";
import { useAppDispatch } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownLong, faArrowUpLong } from "@fortawesome/pro-regular-svg-icons";

import {
  GetSeasonLeaderboard,
  GetSeasonLeaderboardTotals,
  GetSeasonLeaderboardPrint,
  PutSeasonLeaderboard,
  TPutSeasonLeaderboard,
  DeleteSeasonLeaderboard,
} from "api/rpc/2024-04/facilityAdmin/league/scoring/leaderboard";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input";
import Popup from "components/popup/Popup";
import { TLeagueSeasonLeaderboard, TLeagueSeasonLeaderboardTotal } from "redux/reducers/models/league";
import { showError, showSuccess } from "redux/actions/ui";

interface ILeaderboardTotalsState {
  defaultSorted: boolean;
  sortByType: string;
  sortByDirection: boolean;
}

export default function LeaderboardTotals() {
  const { leagueId, leaderboardId } = useParams<{ leagueId: string; leaderboardId: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const [state, setState] = useState<ILeaderboardTotalsState>({
    defaultSorted: false,
    sortByType: "default",
    sortByDirection: false,
  });

  const [leaderboard, setLeaderboard] = useState<TLeagueSeasonLeaderboard>(undefined);
  const [totals, setTotals] = useState<Array<TLeagueSeasonLeaderboardTotal>>(undefined);

  const {
    state: editModal,
    updateModal: updateEditModal,
    closeModal: closeEditModal,
  } = useModal<TPutSeasonLeaderboard>({
    season_leaderboard_id: leaderboardId,
    title: "",
    best_scores: null,
  });

  const { state: deleteModal, updateModal: updateDeleteModal, closeModal: closeDeleteModal } = useModal();

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadLeaderboard(source.token);
    void loadLeaderboardTotals(source.token);

    return () => source.cancel();
  }, []);

  useEffect(() => {
    if (!state.defaultSorted && totals) {
      void sortByDefault();
    }
  }, [totals]);

  async function loadLeaderboard(token?: CancelToken) {
    const leaderboardRes = await GetSeasonLeaderboard(
      { league_id: leagueId, id: leaderboardId },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (leaderboardRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading leaderboard")));
      return;
    }

    setLeaderboard(leaderboardRes.data[0]);
    updateEditModal({
      title: leaderboardRes.data[0].title,
      best_scores: leaderboardRes.data[0].best_scores,
    });
  }

  async function loadLeaderboardTotals(token?: CancelToken) {
    const totalRes = await GetSeasonLeaderboardTotals(
      { season_leaderboard_id: leaderboardId },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (totalRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading leaderboard totals")));
      return;
    }

    setTotals(totalRes.data);
  }

  async function printLeaderboard() {
    const printRes = await GetSeasonLeaderboardPrint({ season_leaderboard_id: leaderboardId }, true);

    if (printRes.status === StatusCode.OK) {
      window.open().document.write(printRes.data);
    } else {
      dispatch(showError(t("Error printing season leaderboard")));
      return;
    }
  }

  async function updateSeasonLeaderboard() {
    const updateRes = await PutSeasonLeaderboard(
      {
        season_leaderboard_id: leaderboardId,
        title: editModal.title,
        best_scores: editModal.best_scores,
      },
      true,
    );

    if (updateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating season leaderboard")));
      return;
    }

    closeEditModal();
    dispatch(showSuccess(t("Season leaderboard updated successfully")));
    void loadLeaderboard();
  }

  async function deleteSeasonLeaderboard() {
    const deleteRes = await DeleteSeasonLeaderboard(
      {
        season_leaderboard_id: leaderboardId,
      },
      true,
    );

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting season leaderboard")));
      return;
    }

    dispatch(showSuccess(t("Season leaderboard deleted successfully")));
    history.push(`/admin/league/${leagueId}/scoring/leaderboards`);
  }

  function sortLeaderboard(sortType: "default" | "bonus" | "participation" | "total" | "overall", sortValue: string) {
    const currentTotals = [...totals];
    let direction: boolean = state.sortByDirection;
    if (state.sortByType !== sortType) {
      direction = true;
    } else if (direction === false) {
      //Descending order, return to default order
      void sortByDefault();
      return;
    } else {
      direction = !direction;
    }

    const sortedTotals = currentTotals.sort((currentTotal, nextTotal) => {
      //Sort null values last
      if (!currentTotal?.[sortValue]) {
        return 1;
      } else if (!nextTotal?.[sortValue]) {
        return -1;
      } else if (sortType == "overall") {
        const currentPlayerPoints = currentTotal?.total + currentTotal?.bonus + currentTotal?.participation;
        const nextPlayerPoints = nextTotal?.total + nextTotal?.bonus + nextTotal?.participation;
        return direction ? nextPlayerPoints - currentPlayerPoints : currentPlayerPoints - nextPlayerPoints;
      } else {
        return direction
          ? nextTotal?.[sortValue] - currentTotal?.[sortValue]
          : currentTotal?.[sortValue] - nextTotal?.[sortValue];
      }
    });
    setState(prevState => ({ ...prevState, sortByType: sortType, sortByDirection: direction }));
    setTotals(sortedTotals);
  }

  function sortByDefault() {
    if (!totals) {
      return;
    }
    const currentTotals = [...totals];
    const sortedTotals = currentTotals?.sort((currentTotal, nextTotal) => {
      if (!currentTotal?.total) {
        return 1;
      } else if (!nextTotal?.total) {
        return -1;
      } else {
        const currentPlayerPoints = currentTotal?.total + currentTotal?.bonus + currentTotal?.participation;
        const nextPlayerPoints = nextTotal?.total + nextTotal?.bonus + nextTotal?.participation;
        return nextPlayerPoints - currentPlayerPoints;
      }
    });

    setState(prevState => ({
      ...prevState,
      sortByType: "overall",
      sortByDirection: true,
      defaultSorted: true,
    }));

    setTotals(sortedTotals);
  }

  const primaryAction = {
    content: t("Print"),
    action: printLeaderboard,
  };

  return (
    <Page
      title={leaderboard?.title ?? ""}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Leaderboards"),
          url: `/admin/league/${leagueId}/scoring/leaderboards`,
        },
      ]}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Edit"),
              handler: () => updateEditModal({ isOpen: true }),
              icon: "pencil",
            },
            {
              type: "handler",
              label: t("Delete"),
              handler: () => updateDeleteModal({ isOpen: true }),
              icon: "trash",
            },
          ],
        },
      }}
    >
      <DataTable
        columns={[
          { label: t("Rank"), width: "5%" },
          { label: leaderboard?.organization === "team" ? t("Team") : t("Player") },
          {
            label: "bonus",
            content: (
              <p onClick={() => sortLeaderboard("bonus", "bonus")} className="leaderboard-header">
                {t("Bonus")}
                <FontAwesomeIcon
                  style={{ visibility: state?.sortByType === "bonus" ? "visible" : "hidden" }}
                  icon={state?.sortByDirection ? faArrowDownLong : faArrowUpLong}
                />
              </p>
            ),
            width: "11%",
          },
          {
            label: "participation",
            content: (
              <p onClick={() => sortLeaderboard("participation", "participation")} className="leaderboard-header">
                {t("Participation")}
                <FontAwesomeIcon
                  style={{ visibility: state?.sortByType === "participation" ? "visible" : "hidden" }}
                  icon={state?.sortByDirection ? faArrowDownLong : faArrowUpLong}
                />
              </p>
            ),
            width: "11%",
          },
          {
            label: "season",
            content: (
              <p onClick={() => sortLeaderboard("total", "total")} className="leaderboard-header">
                {t("Season")}
                <FontAwesomeIcon
                  style={{ visibility: state?.sortByType === "total" ? "visible" : "hidden" }}
                  icon={state?.sortByDirection ? faArrowDownLong : faArrowUpLong}
                />
              </p>
            ),
            width: "11%",
          },
          {
            label: "overall",
            content: (
              <p onClick={() => sortLeaderboard("overall", "overall")} className="leaderboard-header">
                {t("Total")}
                <FontAwesomeIcon
                  style={{ visibility: state?.sortByType === "overall" ? "visible" : "hidden" }}
                  icon={state?.sortByDirection ? faArrowDownLong : faArrowUpLong}
                />
              </p>
            ),
            width: "11%",
          },
        ]}
        loading={totals === undefined}
      >
        {totals?.map((total, index) => (
          <tr key={total.id}>
            <td>{index + 1}</td>
            <td>{total.customer ? total.customer.full_name : total.team?.name}</td>
            <td>{total.bonus}</td>
            <td>{total.participation}</td>
            <td>{total.total}</td>
            <td>{total.total + total.participation + total.bonus}</td>
          </tr>
        ))}
      </DataTable>

      <Portal isMounted={editModal.isOpen}>
        <Sheet
          open={editModal.isOpen}
          title={t("Edit Leaderboard")}
          onCancel={closeEditModal}
          onOk={updateSeasonLeaderboard}
          okText={t("Update")}
          size="small"
          overflow
        >
          <Input
            className="mb-2"
            label={t("Title")}
            value={editModal.title}
            onChange={e => updateEditModal({ title: e.target.value })}
          />
          <Input
            className="mb-2"
            label={t("Scores to Count")}
            value={editModal.best_scores}
            onChange={e => updateEditModal({ best_scores: Number(e.target.value) })}
            type="number"
          />
        </Sheet>
      </Portal>

      <Portal isMounted={deleteModal.isOpen}>
        <Popup
          open={deleteModal.isOpen}
          title={t("Delete Leaderboard")}
          description={t("Are you sure you want to delete this leaderboard")}
          type="warning"
          onOk={deleteSeasonLeaderboard}
          okText={t("Delete")}
          onCancel={closeDeleteModal}
        />
      </Portal>
    </Page>
  );
}
